/**
 * Receive Condition Modal — ถามสภาพของก่อนบันทึก "รับคืนแล้ว"
 *
 * ทำไมต้องมี (feedback ข้ามทีม 2026-09-08):
 * ปุ่ม "รับคืนแล้ว" / "รับครบทั้งใบ" เดิมส่ง `equipment_condition: 'GOOD'` แบบตายตัว
 * ไม่มีทางเลือกอื่นจากปุ่มนั้นเลย → **ของที่กลับมาชำรุดถูกปล่อยเข้าคลังให้เช่าต่อ**
 * (backend อ่าน condition ตรงนี้ไปตั้งสถานะเครื่อง: DAMAGED → DAMAGED, GOOD → AVAILABLE)
 *
 * หลักฐานว่า GOOD-โดยปริยายคือค่าที่ผิดสำหรับงานจริง: ใบตามเก็บบน prod 43 ใบ มีคนแตะจริง
 * แค่ 2 ใบ (#40, #41) และ **ทั้งสองใบกรอกสภาพเป็นชำรุด** — เคสจริงคือของเสีย ไม่ใช่ของดี
 *
 * กติกาที่ยึด:
 * - **ไม่มีค่าตั้งต้น** — สภาพของเป็นสิ่งที่ระบบเดาแทนคนไม่ได้ ค่าเริ่มต้นจะกลายเป็นทางที่ทุกคนเดิน
 * - ให้เลือกแค่ **ดี / ชำรุด** เพราะปุ่มนี้แปลว่า "ของกลับถึงมือแล้ว" — ของที่ตามคืนไม่ได้
 *   ต้องใช้สถานะ "ของหาย" ที่หน้า "ดูรายละเอียด" (มี selector ครบอยู่แล้ว) ไม่ใช่ปิดเป็นรับคืน
 * - หมายเหตุไม่บังคับ: บังคับกรอกจะเพิ่มแรงเสียดทานบนทางที่ซื่อสัตย์ แล้วดันคนกลับไปกด "ดี"
 */

import React, { useEffect, useState } from 'react';
import { RiCheckboxCircleLine, RiErrorWarningLine, RiInformationLine } from 'react-icons/ri';
import Modal from '../ui/Modal';
import LoadingButton from '../ui/LoadingButton';
import { EquipmentCondition } from '../../services/equipmentReturnItemService';
import { handleRadioGroupArrowKeys } from '../../utils/radioGroupKeys';

export type ReceivableCondition = EquipmentCondition.GOOD | EquipmentCondition.DAMAGED;

interface ReceiveConditionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** ข้อความบอกว่ากำลังรับคืนอะไร เช่น รหัสเครื่อง หรือ "ทุกชิ้นในใบตามเก็บนี้" */
  targetLabel: string;
  /** โหมดทั้งใบ — condition ที่เลือกจะใช้กับทุกชิ้น ต้องเตือนให้ชัด */
  isBulk?: boolean;
  /** หัวข้อโมดัล (default ตามโหมด) */
  title?: string;
  /** คำเตือนโหมดทั้งใบ — ต้องชี้ทางออกที่ "มีอยู่จริงบนหน้าที่ผู้ใช้ยืนอยู่"
   *  (หน้ารายละเอียดสัญญาไม่มีปุ่มรายชิ้น การบอกให้ "กดทีละชิ้น" ที่นั่นคือชี้ไปที่ปุ่มที่ไม่มี) */
  bulkHint?: React.ReactNode;
  submitting?: boolean;
  onConfirm: (condition: ReceivableCondition, notes?: string) => void;
}

const OPTIONS: Array<{
  value: ReceivableCondition;
  label: string;
  consequence: string;
  bulkConsequence?: string;
  icon: React.ReactNode;
  activeClass: string;
}> = [
  {
    value: EquipmentCondition.GOOD,
    label: 'สภาพดี',
    consequence: 'กลับเข้าคลัง พร้อมให้เช่าต่อได้ทันที',
    icon: <RiCheckboxCircleLine className="w-5 h-5" />,
    activeClass: 'border-success-500 bg-success-50 text-success-800',
  },
  {
    value: EquipmentCondition.DAMAGED,
    label: 'ชำรุด',
    consequence: 'ไม่กลับเข้าคลัง — เครื่องจะถูกตั้งเป็น "ชำรุด" ให้ตามซ่อมก่อน · ซิมจะถูกตั้งเป็น "ไม่ใช้งาน"',
    // BUG-S2264-001: รับครบทั้งใบใช้สภาพเดียวกับทุกชิ้น — "ชำรุด" หมายถึงเครื่อง ซิมกลับคลังตามปกติ (backend ตรงกัน)
    bulkConsequence: 'เครื่องไม่กลับเข้าคลัง (ตั้งเป็น "ชำรุด") · ซิมในใบนี้กลับเข้าคลังตามปกติ — ถ้าซิมชำรุดด้วย ให้รับคืนแถวซิมทีละแถว',
    icon: <RiErrorWarningLine className="w-5 h-5" />,
    activeClass: 'border-warning-500 bg-warning-50 text-warning-800',
  },
];

const ReceiveConditionModal: React.FC<ReceiveConditionModalProps> = ({
  isOpen,
  onClose,
  targetLabel,
  isBulk = false,
  title,
  bulkHint,
  submitting = false,
  onConfirm,
}) => {
  const [condition, setCondition] = useState<ReceivableCondition | null>(null);
  const [notes, setNotes] = useState('');

  // เปิดใหม่ทุกครั้งต้องเริ่มจาก "ยังไม่เลือก" — ไม่ให้ค่าที่เลือกไว้รอบก่อนติดมา
  useEffect(() => {
    if (isOpen) {
      setCondition(null);
      setNotes('');
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || (isBulk ? 'รับคืนครบทั้งใบ' : 'รับคืนอุปกรณ์')}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50"
          >
            ยกเลิก
          </button>
          <LoadingButton
            type="button"
            loading={submitting}
            disabled={!condition}
            onClick={() => condition && onConfirm(condition, notes.trim() || undefined)}
          >
            บันทึกรับคืน
          </LoadingButton>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-secondary-700">
          ยืนยันว่าได้รับ <span className="font-medium text-secondary-900">{targetLabel}</span> คืนแล้ว
        </p>

        {isBulk && (
          <div className="flex gap-2 p-3 text-sm rounded-lg bg-info-50 text-info-800">
            <RiInformationLine className="flex-shrink-0 w-5 h-5 mt-0.5" />
            <span>
              {bulkHint || (
                <>
                  สภาพที่เลือกจะใช้กับ<strong>ทุกชิ้นในใบนี้</strong> — ถ้าสภาพไม่เหมือนกัน
                  ให้กด "รับคืนแล้ว" ทีละชิ้นแทน
                </>
              )}
            </span>
          </div>
        )}

        <div>
          <div id="receive-condition-label" className="mb-2 text-sm font-medium text-secondary-900">
            สภาพของที่ได้รับคืน <span className="text-danger-600">*</span>
          </div>
          {/* BUG-S2257-002: เดิมสถานะ "เลือกแล้ว" บอกด้วยสีกรอบอย่างเดียว — screen reader ไม่รู้ว่าเลือกอะไร (คลาสเดียวกับ BUG-S2220-001) */}
          <div className="space-y-2" role="radiogroup" aria-labelledby="receive-condition-label" aria-required="true" onKeyDown={handleRadioGroupArrowKeys}>
            {OPTIONS.map((opt) => {
              const active = condition === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setCondition(opt.value)}
                  className={`w-full flex items-start gap-3 p-3 text-left border-2 rounded-lg transition-colors ${
                    active
                      ? opt.activeClass
                      : 'border-secondary-200 hover:border-secondary-300 text-secondary-700'
                  }`}
                >
                  <span className="flex-shrink-0 mt-0.5">{opt.icon}</span>
                  <span>
                    <span className="block text-sm font-medium">{opt.label}</span>
                    <span className="block mt-0.5 text-xs opacity-80">{(isBulk && opt.bulkConsequence) || opt.consequence}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="receive-condition-notes" className="block mb-1 text-sm font-medium text-secondary-900">
            หมายเหตุสภาพของ <span className="text-xs font-normal text-secondary-500">(ไม่บังคับ)</span>
          </label>
          <textarea
            id="receive-condition-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder={
              condition === EquipmentCondition.DAMAGED
                ? 'เช่น จอแตก / แบตเสื่อม / สายชาร์จหาย — ช่วยให้ทีมซ่อมรู้ว่าต้องทำอะไร'
                : 'เช่น รับคืนที่หน้าโครงการ'
            }
            className="w-full px-3 py-2 text-sm border rounded-lg border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <p className="text-xs text-secondary-500">
          ของที่ตามคืนไม่ได้จริง ๆ ให้ใช้สถานะ "ของหาย" ที่ปุ่ม "ดูรายละเอียด" — ไม่ใช่บันทึกว่ารับคืนแล้ว
        </p>
      </div>
    </Modal>
  );
};

export default ReceiveConditionModal;
